import { Draft, Feedback, ReferenceWbr, Run, Team } from "./types";

/**
 * In-memory sample data so the UI is fully explorable before the backend API
 * is wired up. Replace these with API/GraphQL calls (see the AutoWBR design).
 */

export const TEAMS: Team[] = [
  {
    id: "sp-os",
    name: "SP OS",
    description: "Sponsored Products — Advertiser Revenue & Performance",
    schedule: "Weekly · Mon 06:00 PT",
    recipients: 24,
    agentStatus: "DEPLOYED",
    metricProfiles: 3,
    lastRunAt: "2026-06-01T13:02:00Z",
    lastRunStatus: "SUCCESS",
  },
  {
    id: "sp-slim",
    name: "SP Slim",
    description: "Sponsored Products — Slim funnel & shopper metrics",
    schedule: "Weekly · Mon 07:00 PT",
    recipients: 18,
    agentStatus: "DEPLOYED",
    metricProfiles: 2,
    lastRunAt: "2026-06-01T14:10:00Z",
    lastRunStatus: "SUCCESS",
  },
  {
    id: "sb-display",
    name: "SB Display",
    description: "Sponsored Brands — Display & creative performance",
    schedule: "Weekly · Tue 06:00 PT",
    recipients: 11,
    agentStatus: "GENERATING",
    metricProfiles: 2,
    lastRunAt: "2026-05-25T13:00:00Z",
    lastRunStatus: "SUCCESS",
  },
  {
    id: "sd-audiences",
    name: "SD Audiences",
    description: "Sponsored Display — Audiences & retargeting",
    schedule: "Weekly · Wed 06:00 PT",
    recipients: 9,
    agentStatus: "VALIDATION_FAILED",
    metricProfiles: 1,
    lastRunAt: "2026-05-26T13:30:00Z",
    lastRunStatus: "FAILED",
  },
];

export const RUNS: Run[] = [
  {
    id: "run_8f2a91",
    teamId: "sp-os",
    startedAt: "2026-06-01T13:00:00Z",
    finishedAt: "2026-06-01T13:02:00Z",
    status: "SUCCESS",
    trigger: "scheduled",
    stages: [
      { name: "dependency_check", status: "success", durationSec: 14 },
      { name: "data_extraction", status: "success", durationSec: 46 },
      { name: "report_generation", status: "success", durationSec: 52 },
      { name: "distribution", status: "success", durationSec: 8 },
    ],
  },
  {
    id: "run_7c1b40",
    teamId: "sp-os",
    startedAt: "2026-05-25T13:00:00Z",
    finishedAt: "2026-05-25T13:03:00Z",
    status: "SUCCESS",
    trigger: "scheduled",
    stages: [
      { name: "dependency_check", status: "success", durationSec: 16 },
      { name: "data_extraction", status: "success", durationSec: 51 },
      { name: "report_generation", status: "success", durationSec: 60 },
      { name: "distribution", status: "success", durationSec: 9 },
    ],
  },
  {
    id: "run_5a9d22",
    teamId: "sp-os",
    startedAt: "2026-06-04T17:40:00Z",
    finishedAt: null,
    status: "RUNNING",
    trigger: "manual",
    stages: [
      { name: "dependency_check", status: "success", durationSec: 13 },
      { name: "data_extraction", status: "success", durationSec: 44 },
      { name: "report_generation", status: "running", durationSec: null },
      { name: "distribution", status: "pending", durationSec: null },
    ],
  },
  {
    id: "run_3e7f10",
    teamId: "sd-audiences",
    startedAt: "2026-05-26T13:30:00Z",
    finishedAt: "2026-05-26T13:31:00Z",
    status: "FAILED",
    trigger: "scheduled",
    stages: [
      { name: "dependency_check", status: "success", durationSec: 15 },
      { name: "data_extraction", status: "failed", durationSec: 22 },
      { name: "report_generation", status: "skipped", durationSec: null },
      { name: "distribution", status: "skipped", durationSec: null },
    ],
  },
];

export const DRAFTS: Draft[] = [
  {
    id: "draft_os_w23",
    teamId: "sp-os",
    runId: "run_8f2a91",
    weekOf: "2026-06-01",
    status: "PENDING_REVIEW",
    editEffort: 0.12,
    summary:
      "Revenue up 4.2% WoW driven by mobile placements; ACOS steady. Two callouts flagged for narrative review.",
    html: `<h2>SP OS — Weekly Business Review</h2>
<p><strong>Week of June 1, 2026</strong></p>
<h3>Executive summary</h3>
<p>Advertiser revenue grew <strong>4.2% week-over-week</strong>, driven primarily by
mobile search placements. ACOS held flat at 18.3%, and impression supply expanded 2.1%.</p>
<h3>Key metrics</h3>
<table border="1" cellpadding="6" cellspacing="0">
<thead><tr><th>Metric</th><th>This week</th><th>WoW</th></tr></thead>
<tbody>
<tr><td>Advertiser revenue</td><td>$48.2M</td><td>+4.2%</td></tr>
<tr><td>ACOS</td><td>18.3%</td><td>+0.1pp</td></tr>
<tr><td>Impressions</td><td>9.1B</td><td>+2.1%</td></tr>
<tr><td>CTR</td><td>0.41%</td><td>+0.02pp</td></tr>
</tbody>
</table>
<h3>Narrative</h3>
<p>The mobile placement experiment graduated to 100% and is the primary driver of the
revenue lift. Desktop remained flat. Watch the SD cross-over cohort next week.</p>`,
  },
  {
    id: "draft_slim_w23",
    teamId: "sp-slim",
    runId: "run_7c1b40",
    weekOf: "2026-06-01",
    status: "APPROVED",
    editEffort: 0.05,
    summary: "Shopper funnel healthy; no anomalies. Approved with no edits.",
    html: `<h2>SP Slim — Weekly Business Review</h2>
<p><strong>Week of June 1, 2026</strong></p>
<h3>Executive summary</h3>
<p>Slim funnel conversion held at 3.1%. No anomalies detected across the shopper cohorts.</p>`,
  },
];

export const FEEDBACK: Feedback[] = [
  {
    id: "fb_1",
    teamId: "sp-os",
    draftId: "draft_os_w23",
    author: "jdoe",
    createdAt: "2026-06-02T09:14:00Z",
    section: "Narrative",
    comment:
      "Please attribute the lift to the mobile experiment graduation explicitly and add the cohort id.",
    source: "HUMAN",
  },
  {
    id: "fb_2",
    teamId: "sp-os",
    draftId: "draft_os_w23",
    author: "asmith",
    createdAt: "2026-06-02T10:02:00Z",
    section: "Key metrics",
    comment:
      "ACOS should be shown to 2 decimal places to match the source dashboard.",
    source: "HUMAN",
  },
  {
    id: "fb_3",
    teamId: "sp-os",
    draftId: "draft_os_w23",
    author: "Reviewer DAG",
    createdAt: "2026-06-02T08:30:00Z",
    section: "Narrative",
    comment:
      "Automated review: narrative omits the WoW driver attribution present in the reference WBR. Suggested adding one sentence. Escalated to human review.",
    source: "REVIEWER_DAG",
  },
];

export const REFERENCES: ReferenceWbr[] = [
  {
    id: "ref_1",
    teamId: "sp-os",
    title: "OS WBR — FY25 Q4 reference",
    ingestedAt: "2026-05-10T12:00:00Z",
    sizeKb: 184,
    source: "manual upload",
  },
  {
    id: "ref_2",
    teamId: "sp-os",
    title: "OS WBR — peak week exemplar",
    ingestedAt: "2026-05-12T15:30:00Z",
    sizeKb: 211,
    source: "manual upload",
  },
];
